import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const BASE_URL = '/api';

function clientRequest(path, method = 'GET', body) {
  const token = localStorage.getItem('ptm_client_token');
  return fetch(BASE_URL + path, {
    method,
    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(r => r.json());
}

const PORTAL_TABS = ['Program', 'Nutrition', 'Schedule', 'Progress', 'Journal', 'Invoices'];

export default function ClientPortal() {
  const navigate = useNavigate();
  const [client,       setClient]       = useState(null);
  const [tab,          setTab]          = useState('Program');
  const [program,      setProgram]      = useState(null);
  const [nutrition,    setNutrition]    = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [metrics,      setMetrics]      = useState([]);
  const [invoices,     setInvoices]     = useState([]);
  const [nutJournal,   setNutJournal]   = useState([]);
  const [progJournal,  setProgJournal]  = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('ptm_client_token');
    if (!token) { navigate('/client-login'); return; }

    clientRequest('/client-auth/me').then(async c => {
      if (c.message) { navigate('/client-login'); return; }
      setClient(c);

      const [prog, apts, mets, inv, overview] = await Promise.all([
        clientRequest('/programs/client/' + c.id).catch(() => null),
        clientRequest('/appointments').catch(() => []),
        clientRequest('/clients/' + c.id + '/metrics').catch(() => []),
        clientRequest('/invoices').catch(() => []),
        clientRequest('/client/v1/training/overview').catch(() => null),
      ]);

      if (prog?.program_id) {
        const detail = await clientRequest('/programs/' + prog.program_id).catch(() => null);
        setProgram(detail);
      }
      if (overview?.active_nutrition_plan) setNutrition(overview.active_nutrition_plan);
      setAppointments(Array.isArray(apts) ? apts.filter(a => new Date(a.start_time) >= new Date() && a.status === 'scheduled').sort((a,b) => new Date(a.start_time) - new Date(b.start_time)) : []);
      setMetrics(Array.isArray(mets) ? mets : []);
      setInvoices(Array.isArray(inv) ? inv : []);
    }).finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (tab === 'Journal') {
      clientRequest('/client/v1/training/nutrition-journal').then(d => setNutJournal(Array.isArray(d) ? d : [])).catch(() => {});
      clientRequest('/client/v1/training/program-journal').then(d => setProgJournal(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [tab]);

  function logout() {
    localStorage.removeItem('ptm_client_token');
    navigate('/client-login');
  }

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;
  if (!client) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 17 }}>
          <img src="/icons/logo-white.png" alt="" style={{ height: 28 }} onError={e => e.target.style.display='none'} />
          {client.first_name}'s Portal
        </h1>
        <button className="icon-btn" onClick={logout} title="Sign out">
          <img src="/icons/icon-logout.png" alt="Logout" onError={e => e.target.style.display='none'} />
        </button>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)', overflowX: 'auto' }}>
        {PORTAL_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: '0 0 auto', padding: '12px 14px', background: 'transparent', border: 'none',
              borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: tab === t ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {t}
          </button>
        ))}
      </div>

      <div className="page-content" style={{ paddingBottom: 32 }}>
        {tab === 'Program' && (
          !program ? (
            <div className="empty-state">
              <img src="/icons/icon-program.png" alt="" style={{ width: 48, opacity: 0.3 }} onError={e => e.target.style.display='none'} />
              <p>No program assigned yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{program.name}</div>
                {program.goal && <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>{program.goal}</div>}
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: 'var(--color-text-muted)' }}>
                  {program.duration_weeks && <span>{program.duration_weeks} weeks</span>}
                  {program.sessions_per_week && <span>{program.sessions_per_week}x / week</span>}
                </div>
              </div>
              {program.sessions?.map(s => (
                <div key={s.id} className="card">
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{s.session_name || 'Day ' + s.day_number}</div>
                  {s.exercises.map((ex, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
                      <span style={{ fontWeight: 500 }}>{ex.exercise_name}</span>
                      <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                        {ex.sets && ex.reps ? ex.sets + ' x ' + ex.reps : ex.sets ? ex.sets + ' sets' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'Nutrition' && (
          !nutrition ? (
            <div className="empty-state">
              <img src="/icons/icon-nutrition.png" alt="" style={{ width: 48, opacity: 0.3 }} onError={e => e.target.style.display='none'} />
              <p>No nutrition plan assigned yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{nutrition.name || nutrition.plan_name}</div>
                {nutrition.goal && <div style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 8 }}>{nutrition.goal}</div>}
                {nutrition.end_date && (
                  <div style={{ fontSize: 12, color: new Date(nutrition.end_date) < new Date() ? 'var(--color-primary)' : 'var(--color-text-muted)', marginBottom: 8 }}>
                    {new Date(nutrition.end_date) < new Date() ? 'Plan expired' : 'Plan ends'}: {new Date(nutrition.end_date).toLocaleDateString()}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 13 }}>
                  {nutrition.daily_calories  && <span><strong>{nutrition.daily_calories}</strong> kcal target</span>}
                  {nutrition.daily_protein_g && <span>P: <strong>{nutrition.daily_protein_g}g</strong></span>}
                  {nutrition.daily_carbs_g   && <span>C: <strong>{nutrition.daily_carbs_g}g</strong></span>}
                  {nutrition.daily_fat_g     && <span>F: <strong>{nutrition.daily_fat_g}g</strong></span>}
                </div>
                {(nutrition.daily_fibre_g || nutrition.daily_sodium_mg || nutrition.daily_sugar_g) && (
                  <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>
                    {nutrition.daily_fibre_g   && <span>Fibre: {nutrition.daily_fibre_g}g</span>}
                    {nutrition.daily_sodium_mg && <span>Sodium: {nutrition.daily_sodium_mg}mg</span>}
                    {nutrition.daily_sugar_g   && <span>Sugar: {nutrition.daily_sugar_g}g</span>}
                  </div>
                )}
                {nutrition.dietary_restrictions?.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {nutrition.dietary_restrictions.map(r => (
                      <span key={r} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'rgba(215,38,56,0.12)', color: 'var(--color-primary)' }}>{r}</span>
                    ))}
                  </div>
                )}
              </div>

              {(nutrition.meals || []).map((meal, mi) => (
                <div key={mi} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{meal.meal_name}</div>
                    {meal.timing_window && meal.timing_window !== 'general' && (
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
                        {meal.timing_window.replace('-', ' ')}
                      </span>
                    )}
                  </div>
                  {meal.foods?.map((f, fi) => (
                    <div key={fi} style={{ padding: '8px 0', borderTop: fi > 0 ? '1px solid var(--color-border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{f.food_name}</span>
                        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                          {f.quantity && f.unit ? f.quantity + f.unit : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {f.calories  && <span>{f.calories} kcal</span>}
                        {f.protein_g && <span>P: {f.protein_g}g</span>}
                        {f.carbs_g   && <span>C: {f.carbs_g}g</span>}
                        {f.fat_g     && <span>F: {f.fat_g}g</span>}
                        {f.fibre_g   && <span>Fibre: {f.fibre_g}g</span>}
                        {f.sugar_g   && <span>Sugar: {f.sugar_g}g</span>}
                      </div>
                      {f.notes && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, fontStyle: 'italic' }}>{f.notes}</div>}
                    </div>
                  ))}
                </div>
              ))}

              {nutrition.notes && (
                <div className="card">
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes from your trainer</div>
                  <div style={{ fontSize: 14, color: 'var(--color-text-muted)', whiteSpace: 'pre-line' }}>{nutrition.notes}</div>
                </div>
              )}
            </div>
          )
        )}

        {tab === 'Schedule' && (
          appointments.length === 0 ? (
            <div className="empty-state">
              <img src="/icons/icon-calendar.png" alt="" style={{ width: 48, opacity: 0.3 }} onError={e => e.target.style.display='none'} />
              <p>No upcoming appointments</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {appointments.map(a => (
                <div key={a.id} className="card">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {new Date(a.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <img src="/icons/icon-clock.png" alt="" style={{ width: 12 }} onError={e => e.target.style.display='none'} />
                    {new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(a.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {a.location && <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>{a.location}</div>}
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'Progress' && (
          metrics.length === 0 ? (
            <div className="empty-state">
              <img src="/icons/icon-chart.png" alt="" style={{ width: 48, opacity: 0.3 }} onError={e => e.target.style.display='none'} />
              <p>No metrics tracked yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {metrics.map(m => <PortalMetricChart key={m.id} metric={m} />)}
            </div>
          )
        )}

        {tab === 'Journal' && (
          <JournalTab
            client={client}
            nutrition={nutrition}
            program={program}
            nutJournal={nutJournal}
            progJournal={progJournal}
            onNutSaved={entry => setNutJournal(prev => {
              const idx = prev.findIndex(e => e.journal_date === entry.journal_date);
              return idx >= 0 ? prev.map((e, i) => i === idx ? entry : e) : [entry, ...prev];
            })}
            onProgSaved={entry => setProgJournal(prev => {
              const idx = prev.findIndex(e => e.journal_date === entry.journal_date);
              return idx >= 0 ? prev.map((e, i) => i === idx ? entry : e) : [entry, ...prev];
            })}
          />
        )}

        {tab === 'Invoices' && (
          invoices.length === 0 ? (
            <div className="empty-state">
              <img src="/icons/icon-invoice.png" alt="" style={{ width: 48, opacity: 0.3 }} onError={e => e.target.style.display='none'} />
              <p>No invoices yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {invoices.map(inv => (
                <div key={inv.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      {inv.description && <div style={{ fontWeight: 600 }}>{inv.description}</div>}
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
                        {new Date(inv.created_at).toLocaleDateString()}
                        {inv.due_date && ' · Due ' + new Date(inv.due_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>${parseFloat(inv.amount).toFixed(2)}</div>
                      <div style={{ fontSize: 12, textTransform: 'capitalize', fontWeight: 600, marginTop: 2,
                        color: inv.status === 'paid' ? 'var(--color-success)' : inv.status === 'overdue' ? 'var(--color-primary)' : 'var(--color-warning)' }}>
                        {inv.status}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function JournalTab({ client, nutrition, program, nutJournal, progJournal, onNutSaved, onProgSaved }) {
  const [section, setSection] = useState('nutrition');
  const today = new Date().toISOString().split('T')[0];

  const [nutForm, setNutForm] = useState({
    journal_date: today, note: '', actual_calories: '', actual_protein_g: '',
    actual_carbs_g: '', actual_fat_g: '', adherence_score: '',
  });
  const [progForm, setProgForm] = useState({
    journal_date: today, completed: false, note: '', rating: '',
  });
  const [nutSaving,  setNutSaving]  = useState(false);
  const [progSaving, setProgSaving] = useState(false);
  const [nutMsg,     setNutMsg]     = useState('');
  const [progMsg,    setProgMsg]    = useState('');

  async function handleNutSubmit(e) {
    e.preventDefault();
    if (!nutrition) return;
    setNutSaving(true);
    try {
      const result = await clientRequest('/client/v1/training/nutrition-journal', 'POST', {
        plan_id: nutrition.plan_id,
        ...nutForm,
        actual_calories:  nutForm.actual_calories  || null,
        actual_protein_g: nutForm.actual_protein_g || null,
        actual_carbs_g:   nutForm.actual_carbs_g   || null,
        actual_fat_g:     nutForm.actual_fat_g     || null,
        adherence_score:  nutForm.adherence_score  || null,
      });
      onNutSaved(result);
      setNutMsg('Journal entry saved!');
      setTimeout(() => setNutMsg(''), 3000);
    } catch {}
    setNutSaving(false);
  }

  async function handleProgSubmit(e) {
    e.preventDefault();
    if (!program) return;
    setProgSaving(true);
    try {
      const result = await clientRequest('/client/v1/training/program-journal', 'POST', {
        program_id: program.id,
        ...progForm,
        rating: progForm.rating || null,
      });
      onProgSaved(result);
      setProgMsg('Journal entry saved!');
      setTimeout(() => setProgMsg(''), 3000);
    } catch {}
    setProgSaving(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: 'var(--color-surface-2)', borderRadius: 10, padding: 4 }}>
        {['nutrition', 'program'].map(s => (
          <button key={s} type="button" onClick={() => setSection(s)}
            style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
              background: section === s ? 'var(--color-surface)' : 'transparent',
              color: section === s ? 'var(--color-text)' : 'var(--color-text-muted)',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: section === s ? 600 : 400, cursor: 'pointer' }}>
            {s === 'nutrition' ? 'Nutrition' : 'Workout'}
          </button>
        ))}
      </div>

      {section === 'nutrition' && (
        <div>
          {!nutrition ? (
            <div className="empty-state"><p>No nutrition plan assigned yet</p></div>
          ) : (
            <>
              {nutMsg && <div className="success-msg" style={{ marginBottom: 12 }}>{nutMsg}</div>}
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Log Today's Nutrition</div>
                <form onSubmit={handleNutSubmit}>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input className="form-input" type="date" value={nutForm.journal_date}
                      onChange={e => setNutForm(f => ({ ...f, journal_date: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                    {[['Calories eaten', 'actual_calories'], ['Protein (g)', 'actual_protein_g'], ['Carbs (g)', 'actual_carbs_g'], ['Fat (g)', 'actual_fat_g']].map(([label, key]) => (
                      <div className="form-group" key={key}>
                        <label className="form-label">{label}</label>
                        <input className="form-input" type="number" min="0" value={nutForm[key]}
                          onChange={e => setNutForm(f => ({ ...f, [key]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Adherence % (how closely did you follow the plan?)</label>
                    <input className="form-input" type="number" min="0" max="100" placeholder="e.g. 85"
                      value={nutForm.adherence_score}
                      onChange={e => setNutForm(f => ({ ...f, adherence_score: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={3} placeholder="What did you eat? Any challenges today?"
                      value={nutForm.note} onChange={e => setNutForm(f => ({ ...f, note: e.target.value }))} />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={nutSaving} style={{ width: '100%' }}>
                    {nutSaving ? 'Saving...' : 'Save Nutrition Log'}
                  </button>
                </form>
              </div>
              {nutJournal.length > 0 && (
                <div className="card">
                  <div style={{ fontWeight: 600, marginBottom: 10 }}>Past Entries</div>
                  {nutJournal.map(e => (
                    <div key={e.id} style={{ padding: '8px 0', borderTop: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{e.journal_date}</span>
                        {e.adherence_score != null && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: e.adherence_score >= 80 ? 'var(--color-success)' : e.adherence_score >= 50 ? 'var(--color-warning)' : 'var(--color-primary)' }}>
                            {e.adherence_score}% adherence
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {e.actual_calories  && <span>{e.actual_calories} kcal</span>}
                        {e.actual_protein_g && <span>P: {e.actual_protein_g}g</span>}
                        {e.actual_carbs_g   && <span>C: {e.actual_carbs_g}g</span>}
                        {e.actual_fat_g     && <span>F: {e.actual_fat_g}g</span>}
                      </div>
                      {e.note && <div style={{ fontSize: 13, marginTop: 4 }}>{e.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {section === 'program' && (
        <div>
          {!program ? (
            <div className="empty-state"><p>No program assigned yet</p></div>
          ) : (
            <>
              {progMsg && <div className="success-msg" style={{ marginBottom: 12 }}>{progMsg}</div>}
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Log Today's Workout</div>
                <form onSubmit={handleProgSubmit}>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input className="form-input" type="date" value={progForm.journal_date}
                      onChange={e => setProgForm(f => ({ ...f, journal_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="checkbox" checked={progForm.completed}
                        onChange={e => setProgForm(f => ({ ...f, completed: e.target.checked }))} />
                      Workout completed
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rating (1-5)</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button"
                          onClick={() => setProgForm(f => ({ ...f, rating: f.rating === n ? '' : n }))}
                          style={{ width: 40, height: 40, borderRadius: 8, border: 'none',
                            background: progForm.rating >= n ? 'var(--color-primary)' : 'var(--color-surface-2)',
                            color: progForm.rating >= n ? '#fff' : 'var(--color-text-muted)',
                            fontFamily: 'var(--font)', fontSize: 16, cursor: 'pointer' }}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={3} placeholder="How did the workout go? Any notes for your trainer?"
                      value={progForm.note} onChange={e => setProgForm(f => ({ ...f, note: e.target.value }))} />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={progSaving} style={{ width: '100%' }}>
                    {progSaving ? 'Saving...' : 'Save Workout Log'}
                  </button>
                </form>
              </div>
              {progJournal.length > 0 && (
                <div className="card">
                  <div style={{ fontWeight: 600, marginBottom: 10 }}>Past Entries</div>
                  {progJournal.map(e => (
                    <div key={e.id} style={{ padding: '8px 0', borderTop: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{e.journal_date}</span>
                        <span style={{ fontSize: 12, color: e.completed ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                          {e.completed ? 'Completed' : 'Partial'}
                        </span>
                      </div>
                      {e.rating && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                          Rating: {'*'.repeat(e.rating) + 'o'.repeat(5 - e.rating)}
                        </div>
                      )}
                      {e.note && <div style={{ fontSize: 13 }}>{e.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PortalMetricChart({ metric }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    clientRequest('/sessions/metrics/' + metric.id + '/history').then(data => {
      setHistory(data.map(d => ({
        date:  new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(d.value),
      })));
    }).catch(() => {});
  }, [metric.id]);

  if (history.length === 0) return null;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <img src="/icons/icon-chart.png" alt="" style={{ width: 16, height: 16 }} onError={e => e.target.style.display='none'} />
        <span style={{ fontWeight: 600 }}>{metric.exercise_name}</span>
        {metric.unit && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{metric.unit}</span>}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={history} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2E2E2E" />
          <XAxis dataKey="date" tick={{ fill: '#AAAAAA', fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: '#AAAAAA', fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2E2E2E', borderRadius: 8, color: '#fff' }} labelStyle={{ color: '#AAAAAA', fontSize: 11 }} />
          <Line type="monotone" dataKey="value" stroke="#D72638" strokeWidth={2} dot={{ fill: '#D72638', r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
